import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import type { Event } from "@circle-react/types/Event";
import { CommunityMemberPicker } from "@circle-react-shared/CommunityMemberPicker";
import type { Value } from "@circle-react-shared/CommunityMemberPicker/types";
import { Form } from "@circle-react-uikit/Form";
import { Typography } from "@circle-react-uikit/Typography";
import { FormItem } from "../../../Common";
import { useEventsContext } from "../../../EditEventContext";
import { useEventAttendeesApi } from "../../../hooks/useEventAttendeesApi";
import { ATTENDEE_ROLES } from "../../../utils";
import { AttendeesWarningMessage } from "../AttendeesWarningMessage";
import { List } from "../List";
import { Pagination } from "../Pagination";

interface AttendeesProps {
  event: Event;
}

export const Attendees = ({ event }: AttendeesProps) => {
  const {
    records,
    refetch,
    fetchPrevPage,
    fetchNextPage,
    hasPrevPage,
    hasNextPage,
    totalRecordCount,
    totalConfirmedRecordCount,
    addAttendeesAsync,
    startRecordNumber,
    endRecordNumber,
  } = useEventAttendeesApi(event, ATTENDEE_ROLES.PARTICIPANT);

  const { resetField, watch } = useFormContext();
  const { setIsAutoRsvpInvitedAttendees } = useEventsContext();

  // Watch the auto_rsvp_invited_attendees toggle value and update the context
  const autoRsvpValue = watch(
    "event_setting_attributes.auto_rsvp_invited_attendees",
  );

  // Update the context whenever the toggle value changes
  useEffect(() => {
    setIsAutoRsvpInvitedAttendees(autoRsvpValue || false);
  }, [autoRsvpValue, setIsAutoRsvpInvitedAttendees]);

  const onAddAttendees = async (entityIds: Value) => {
    if (!entityIds) {
      return;
    }
    if (
      "spaces_ids" in entityIds &&
      !entityIds.spaces_ids.length &&
      !entityIds.space_groups_ids.length &&
      !entityIds.member_tags_ids.length &&
      !entityIds.members_ids.length
    ) {
      return;
    }
    try {
      await addAttendeesAsync({ invited_entities_ids: entityIds });
      resetField("attendee_invite_attributes.invited_entities_ids");
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <div className="flex h-full flex-col items-stretch pt-4">
      <div className="flex w-full items-center justify-between">
        <Typography.LabelMd weight="semibold">
          {`${totalConfirmedRecordCount || 0} ${t(
            "events.edit_modal.people.attendees",
          )}`}
        </Typography.LabelMd>
        <FormItem
          name="attendee_invite_attributes.invited_entities_ids"
          className="!w-auto"
          hideLabel
        >
          <CommunityMemberPicker
            name="attendee_invite_attributes.invited_entities_ids"
            variant="button"
            buttonLabel={t("events.edit_modal.people.add_attendees")}
            selectionLabel={t(
              "events.edit_modal.people.attendees_selection_label",
            )}
            searchPlaceholder={t(
              "events.edit_modal.people.attendees_placeholder",
            )}
            modalSaveButtonLabel={t("save")}
            onModalSave={onAddAttendees}
            renderAfterSelection={<AttendeesWarningMessage />}
            includeAllResultTypes
          />
        </FormItem>
      </div>
      <div className="mb-3 mt-2">
        <FormItem
          name="event_setting_attributes.auto_rsvp_invited_attendees"
          tooltipText={
            event?.disable_auto_rsvp_attendees
              ? t(
                  "events.edit_modal.event_setting_attributes.auto_rsvp_invited_attendees_tooltip_disabled",
                )
              : t(
                  "events.edit_modal.event_setting_attributes.auto_rsvp_invited_attendees_tooltip",
                )
          }
          inlineReverse
          className="!pb-0"
          tooltipTextClassName="text-center"
          labelClassName={
            event?.disable_auto_rsvp_attendees ? "text-tertiary" : ""
          }
        >
          <Form.ToggleSwitch
            name="event_setting_attributes.auto_rsvp_invited_attendees"
            variant="small"
            disabled={event?.disable_auto_rsvp_attendees}
          />
        </FormItem>
      </div>
      <div className="mt-4" />
      {records?.length > 0 && (
        <List event={event} records={records} onChange={refetch} />
      )}
      {totalRecordCount > 0 && (
        <Pagination
          startRecordNumber={startRecordNumber}
          endRecordNumber={endRecordNumber}
          totalRecordCount={totalRecordCount}
          fetchPrevPage={fetchPrevPage}
          fetchNextPage={fetchNextPage}
          hasPrevPage={hasPrevPage}
          hasNextPage={hasNextPage}
        />
      )}
    </div>
  );
};
